import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import OrderAPI from '@/apps/order/classes/api/OrderAPI';
import FormDataCalculatorInterface from '@/apps/order/interfaces/ordersCreate/FormDataCalculatorInterface';
import { autoUpdateSessionStorage, reactivityFix } from '@/apps/order/helper';

import { useRouterStore } from '@/apps/order/stores';

import BrandSettingsInterface from '@/apps/order/interfaces/BrandSettingsInterface';
import BonusKindInterface from '@/apps/order/interfaces/BonusKindInterface';
import BrandValueInterface from '@/apps/order/interfaces/BrandValueInterface';
import CustomerTypeInterface from '@/apps/order/interfaces/CustomerTypeInterface';
import TariffInterface from '@/apps/order/interfaces/TariffInterface';
import DataPreparedCalculatorInterface from '@/apps/order/interfaces/ordersCreate/DataPreparedCalculatorInterface';
import { PriceUpdateParamsInterface, PriceUpdateVoucherParamsInterface } from '@/apps/order/interfaces/ordersCreate/PriceUpdateParamsInterface';
import EnetTariffInterface from '@/apps/order/interfaces/EnetTariffInterface';

export interface AllowedOrderCompletionTypes {
  incomplete?: boolean;
  customerIncomplete?: boolean;
  email?: boolean;
  signature?: boolean;
  document?: boolean;
  documentPassword?: boolean;
  letter?: boolean;
}

export interface SalesPartnerNumber {
  label: string;
  value: string;
  allowedOrderCompletionTypes?: AllowedOrderCompletionTypes;
}
export interface SalesChannel {
  label: string;
  value: number;
  allowedOrderCompletionTypes?: AllowedOrderCompletionTypes;
  partnerNumbers?: SalesPartnerNumber[];
}

const useOrdersCreateCalculatorStore = defineStore('ordersCreateCalculator', () => {
  const routerStore = useRouterStore();
  const { meta } = storeToRefs(routerStore);

  const formData = ref<Record<string, any>>({
    postcode: '',
    city: '',
    street: '',
    number: '',
    type: '',
    customer_type: 'private',
    brand_id: '',
    salesChannel: '',
    excludedBonus: '',
    sales_channel_id: '',
    counterType: {},
    measurementType: {},
    subcategories: {},
    usage: {},
    usageHt: {},
    usageNt: {},
    supplier: {},
    supplier_tariff: {},
    source: null,
  });

  const currentTariffsCalculationParams = ref<FormDataCalculatorInterface>({
    ...(formData.value as unknown as FormDataCalculatorInterface),
  });

  const updateCurrentTariffsCalculationParams = (data: FormDataCalculatorInterface): void => {
    currentTariffsCalculationParams.value = {
      ...data,
    };
  };

  reactivityFix(formData);
  autoUpdateSessionStorage(`${meta.value.ctx}.calculatorFormData`, () => formData.value, 1000);

  const selectedTariffs = ref<Record<string, any>>({});
  reactivityFix(selectedTariffs);
  autoUpdateSessionStorage(`${meta.value.ctx}.calculatorSelectedTariffs`, () => selectedTariffs.value, 1000);

  const transitionTariffs = ref<TariffInterface[]>([]);
  autoUpdateSessionStorage(`${meta.value.ctx}.calculatorTransitionTariffs`, () => transitionTariffs.value, 1000);

  const selectableBonuses = ref<{
    perCategory: Record<string, BonusKindInterface[]>,
    perProduct: Record<string, BonusKindInterface[]>,
  }>({
    perCategory: {},
    perProduct: {},
  });
  const selectedBonuses = ref<{
    perCategory: Record<string, number[]>,
    perProduct: Record<string, number[]>,
  }>({
    perCategory: {},
    perProduct: {},
  });
  reactivityFix(selectedBonuses);
  autoUpdateSessionStorage(`${meta.value.ctx}.ordersCreate.calculatorSelectedBonuses`, () => selectedBonuses.value, 1000);

  const updatedPrices = ref<{
    [key: string]: PriceUpdateParamsInterface | PriceUpdateVoucherParamsInterface,
  }>({});
  reactivityFix(updatedPrices);
  autoUpdateSessionStorage(`${meta.value.ctx}.ordersCreate.calculatorUpdatedPrices`, () => updatedPrices.value, 1000);

  // transient UI state: product uuids whose prices are currently being recalculated
  const pricesUpdating = ref<Record<string, boolean>>({});

  const cities = ref<object[]>([]);
  const streets = ref<object[]>([]);
  autoUpdateSessionStorage(`${meta.value.ctx}.calculatorStreets`, () => streets.value, 1000);

  const suppliers = ref<object[]>([]);
  const supplierTariffs = ref<object[]>([]);

  const brands = ref<BrandValueInterface[]>([]);
  const brandsSettings = ref<Record<number, BrandSettingsInterface>>({});
  const salesChannels = ref<SalesChannel[]>([]);
  const customerTypes = ref<CustomerTypeInterface[]>();
  const bonuses = ref<BonusKindInterface[]>([]);
  const counterTypes = ref<{ value: 'single' | 'double', label: string }[]>([
    {
      value: 'single',
      label: 'Eintarif',
    },
    {
      value: 'double',
      label: 'Zweitarif',
    },
  ]);
  const measurementTypes = ref<object[]>([
    {
      value: 'doubleTariffWithoutHouseholdPower',
      label: 'Zweitarif ohne Haushaltsstrom',
    },
    {
      value: 'doubleTariffWithHouseholdPower',
      label: 'Zweitarif mit Haushaltsstrom',
    },
  ]);

  const priceDetailsExtendedActive = ref<boolean>(false);
  const altProductPresentationActive = ref<boolean>(false);

  const dataPreparedFetched = ref<boolean>(false);

  const fetchedSalesChannels = ref<boolean>(false);
  const fetchSalesChannels = async (brandId: number | undefined): Promise<SalesChannel[]> => {
    const isValidId = brandId !== undefined && `${brandId ?? ''}`.trim() !== '';

    if (!window.API_URLS.brandSalesChannels || !isValidId) {
      salesChannels.value = [];
    } else {
      const responseData = await OrderAPI.fetchSalesChannels(brandId) ?? [];

      salesChannels.value = responseData as unknown as SalesChannel[];
    }

    fetchedSalesChannels.value = true;

    return salesChannels.value;
  };

  const updateByDataPrepared = (data: DataPreparedCalculatorInterface) => {
    brands.value = data.brands as BrandValueInterface[];
    brandsSettings.value = data.brandsSettings;
    customerTypes.value = data.customerTypes as CustomerTypeInterface[];

    if (
      data.salesChannel !== null
      && data.salesChannel !== undefined
      && data.salesChannel.id !== undefined
    ) {
      formData.value.sales_channel_id = data.salesChannel.id as number;
    }

    priceDetailsExtendedActive.value = data.priceDetailsExtendedActive;
    altProductPresentationActive.value = data.altProductPresentationActive;

    dataPreparedFetched.value = true;
  };

  const priceFilePricesUuid = ref<string | undefined>();
  const priceFileURL = ref<string | undefined>();

  // Current contract tariff for tariff change mode
  const currentContractTariff = ref<EnetTariffInterface | undefined>();
  autoUpdateSessionStorage(`${meta.value.ctx}.currentContractTariff`, () => currentContractTariff.value, 1000);

  const allowedOrderCompletionTypes = computed((): AllowedOrderCompletionTypes => {
    const salesChannel = salesChannels.value
      .find((channel) => channel.value === formData.value.sales_channel_id);

    const salesPartnerNumber = salesChannel?.partnerNumbers
      ?.find((partner) => partner.value === formData.value.salesPartnerNumber);

    return {
      incomplete: true,
      customerIncomplete: true,
      email: true,
      signature: true,
      document: true,
      documentPassword: true,
      letter: true,
      ...(salesChannel?.allowedOrderCompletionTypes ?? {}),
      ...(salesPartnerNumber?.allowedOrderCompletionTypes ?? {}),
    };
  });

  return {
    formData,
    currentTariffsCalculationParams,
    updateCurrentTariffsCalculationParams,
    cities,
    streets,
    brands,
    brandsSettings,
    fetchedSalesChannels,
    fetchSalesChannels,
    salesChannels,
    customerTypes,
    bonuses,
    counterTypes,
    measurementTypes,
    suppliers,
    supplierTariffs,
    dataPreparedFetched,
    selectedTariffs,
    transitionTariffs,
    selectableBonuses,
    selectedBonuses,
    updatedPrices,
    pricesUpdating,
    priceDetailsExtendedActive,
    altProductPresentationActive,
    priceFilePricesUuid,
    priceFileURL,
    currentContractTariff,
    allowedOrderCompletionTypes,
    updateByDataPrepared,
  };
});

export default useOrdersCreateCalculatorStore;
